import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import MicOffIcon from "@mui/icons-material/MicOff";
import MicIcon from "@mui/icons-material/Mic";
import CallEndIcon from "@mui/icons-material/CallEnd";
import { selectContact } from "../../../state/contact/contactSlice";
import { getDisplayName } from "../../../utils/contactUtils";
import { formatPhone } from "../../../utils/phoneUtils";
import { muteConferenceParticipantAction } from "../../../sagas/conferenceCall/muteConferenceParticipant/action";
import { unmuteConferenceParticipantAction } from "../../../sagas/conferenceCall/unmuteConferenceParticipant/action";
import { removeConferenceParticipantAction } from "../../../sagas/conferenceCall/removeConferenceParticipant/action";


const CallStatus = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginRight: theme.spacing(5),
  marginBottom: theme.spacing(1),
}));

const ParticipantsContainer = styled(Box)(({ theme }) => ({
  height: "130px",
  overflowY: "auto",
  marginBottom: theme.spacing(4),
  marginLeft: theme.spacing(-2),
  marginRight: theme.spacing(-2),
  padding: theme.spacing(0, 2),
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: theme.palette.background.default,
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.secondary.main,
    borderRadius: "4px",
  },
}));

const ParticipantCard = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2, 2, 2, 1),
  borderRadius: "8px",
  backgroundColor: theme.palette.secondary.dark,
  minHeight: "110px",
}));

const ParticipantInfo = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flexGrow: 1,
  minWidth: 0,
}));

const ParticipantDetails = styled(Stack)({
  marginLeft: "8px",
  minWidth: 0,
  flexGrow: 1,
});

const ParticipantActions = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "5px",
  flexShrink: 0,
});

const SmallIconButton = styled(IconButton)(({ theme }) => ({
  "& .MuiSvgIcon-root": {
    fontSize: 20,
  },
  padding: theme.spacing(0.5),
}));

const TruncatedTypography = styled(Typography)(({ theme }) => ({
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  zIndex: theme.zIndex.tooltip,
  cursor: "pointer",
}));

const extractUsername = (sipUri) => {
  const match = sipUri.match(/sip:(\d+)/);
  return match ? match[1] : sipUri;
};

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}`;
};

const ConferenceCallHeader = ({ activeCall }) => {
  const { contact } = useSelector(selectContact);
  const dispatch = useDispatch();

  const handleMuteToggle = (participant) => {
    if (participant.mode === "muted") {
      dispatch(
        unmuteConferenceParticipantAction({
          conferenceUri: activeCall.session.remoteIdentity.uri.toString(),
          participantId: participant.participant_match,
        })
      );
    } else {
      dispatch(
        muteConferenceParticipantAction({
          conferenceUri: activeCall.session.remoteIdentity.uri.toString(),
          participantId: participant.participant_match,
        })
      );
    }
  };

  const handleRemoveParticipant = (participant) => {
    dispatch(
      removeConferenceParticipantAction({
        conferenceUri: activeCall.session.remoteIdentity.uri.toString(),
        participantId: participant.participant_match,
      })
    );
  };

  return (
    <>
      <CallStatus>
        <Typography variant="h4" color="common.white">
          Conference Call
        </Typography>
        <Typography variant="body1" color="common.white">
          {formatTime(activeCall?.callDuration || 0)}
        </Typography>
      </CallStatus>
      <ParticipantsContainer>
        <Grid container spacing={1.5}>
          {activeCall?.conferenceList.map((participant, index) => (
            <Grid item xs={6} key={index}>
              <ParticipantCard>
                <ParticipantInfo>
                  <AccountCircleRoundedIcon
                    sx={{ fontSize: 40, flexShrink: 0 }}
                  />
                  <ParticipantDetails spacing={0.5}>
                    <Tooltip
                      title={getDisplayName(
                        contact,
                        extractUsername(participant.participant_match)
                      )}
                      arrow
                    >
                      <TruncatedTypography variant="subtitle1">
                        {getDisplayName(
                          contact,
                          extractUsername(participant.participant_match)
                        )}
                      </TruncatedTypography>
                    </Tooltip>
                    <Tooltip
                      title={formatPhone(participant.participant_match)}
                      arrow
                    >
                      <TruncatedTypography variant="body2">
                        {formatPhone(participant.participant_match)}
                      </TruncatedTypography>
                    </Tooltip>
                  </ParticipantDetails>
                </ParticipantInfo>
                <ParticipantActions>
                  <SmallIconButton
                    onClick={() => handleMuteToggle(participant)}
                    size="small"
                    sx={{ margin: 0 }}
                  >
                    {participant.mode === "muted" ? (
                      <MicOffIcon />
                    ) : (
                      <MicIcon />
                    )}
                  </SmallIconButton>
                  <SmallIconButton
                    onClick={() => handleRemoveParticipant(participant)}
                    size="small"
                    sx={{ margin: 0 }}
                  >
                    <CallEndIcon />
                  </SmallIconButton>
                </ParticipantActions>
              </ParticipantCard>
            </Grid>
          ))}
        </Grid>
      </ParticipantsContainer>
    </>
  );
};
export default ConferenceCallHeader;
